import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Highcharts from 'highcharts';
import Dashboards from '@highcharts/dashboards';
import DataGrid from '@highcharts/dashboards/datagrid';
import LayoutModule from '@highcharts/dashboards/modules/layout';
import '@highcharts/dashboards/css/dashboards.css';
import '@highcharts/dashboards/css/datagrid.css';
import 'highcharts/css/highcharts.css';
import { StyledTile } from '../Dashboard';
import './InsightsDashboard.scss';
import Dashboard from './Components/Dashboard';
import { DashboardContextProvider } from './DashboardContext';
import MockData from './Utils/MockData';
import MockAppointmentData from './Utils/MockAppointmentData';
import { GET_CAREPLANS_QUERY, DOCTOR_APPOINTMENT_QUERY, INSIGHTS_USER_QUERY, TENANT_CONFIG_QUERY } from './queries';
import DashboardOverview from './Components/DashboardOverview';
import Reports from './Components/Reports';
import exporting from 'highcharts/modules/exporting';
import exportData from 'highcharts/modules/export-data';
import { DataFunctions } from './Utils/dataFunctions';
import * as Config from './Components/DashboardConfigs';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import NavBar from './Components/NavBar';
import { DEFAULT_INSIGHTS_START_DATE, isTreatmentAppointment, isSimAppointment } from './Utils/utils';
import { isDemo } from 'op-utils';
import { PermissionType } from 'op-enums';
import { UserContext } from 'op-contexts';
import { TenantConfigType } from './Utils/utils';
import dayjs from 'dayjs';

const LOCAL_ENV = import.meta.env.VITE_LOCAL_ENV;

// Highcharts setup
LayoutModule(Dashboards);
exporting(Highcharts);
exportData(Highcharts);
Dashboards.HighchartsPlugin.custom.connectHighcharts(Highcharts);
Dashboards.DataGridPlugin.custom.connectDataGrid(DataGrid);
Dashboards.PluginHandler.addPlugin(Dashboards.HighchartsPlugin);
Dashboards.PluginHandler.addPlugin(Dashboards.DataGridPlugin);
Highcharts.setOptions({
  lang: {
    contextButtonTitle: 'Download data',
  },
});

type SectionNames = 'Quality' | 'Access' | 'Efficiency' | 'Activity';

// DATA PROCESSING FUNCTIONS
const processMockData = (mockData: any[], mockAppointmentData: any[]) => {
  const startDate = dayjs('2025-06-01');
  const offsetDays = dayjs().diff(startDate, 'day');
  const processedCareplanData = mockData.map((record) => ({
    ...record,
    submittedAt: dayjs(record.submittedAt).add(offsetDays, 'day').format('MM/DD/YYYY'),
    description: '',
    __typename: 'CarepathCareplanType',
  }));

  const processedAppointmentsData = mockAppointmentData.map((record) => {
    const submittedAt = dayjs(record.submittedAt).add(offsetDays, 'day');
    return {
      appointment: {
        patient: { id: record.patientId },
        location: { name: record.location },
        submittedAt: submittedAt,
        appointmentType: record.appointmentType,
        description: record.description,
        activity: record.appointmentType,
      },
      submittedAt: submittedAt,
      createdBy: record.physician,
      tumourStream: 'Unspecified',
      treatingDepartment: record.treatingDepartment,
      relatedPractitioners: [
        {
          practitioner: {
            firstName: record.physician,
            lastName: record.physician,
          },
          relationshipType: 'Oncologist',
        },
      ],
      __typename: 'AppointmentWithPractitionersType',
    };
  });

  return [...processedCareplanData, ...processedAppointmentsData];
};

const processCareplans = (careplanData: any[], tenantConfig: TenantConfigType) => {
  if (!careplanData) {
    return [];
  }

  const getSiteValue = (siteValues: any[], name: string, defaultValue: any = '') => {
    return (
      siteValues
        .find((siteValue: { field: { name: string } }) => siteValue.field.name === name)
        ?.value.replace(/"/g, '') || defaultValue
    );
  };

  const normalizeTumourStream = (tumourStream: string): string => {
    switch (tumourStream) {
      case 'Miscellaneous':
        return 'Misc';
      case 'GI, Colo-rectal + Abdo':
        return 'GI';
      case 'Sarcoma - Soft Tissue':
        return 'Sarcoma';
      default:
        return tumourStream;
    }
  };

  // grouping similar techniques to reduce the number of categories:
  //    - '3D Conformal ARC', '3D', 'Electrons', 'Electron CT Plan', 'Simple', 'Clinical', 'Conformal RT' = 'Conformal'
  //    - 'Brachy - HDR', 'Brachy - LDR' and 'Brachy Seeds' = 'Brachy'
  //    - 'SBRT VMAT', 'SBRT IMRT', 'SRS IMRT', 'SRS VMAT' = 'Stereotactic'
  //    - 'VMAT', 'IMRT', 'VMAT/IMRT', 'Tangential IMRT' = 'IMRT/VMAT'
  //    - 'Complex' = 'Complex'
  //    - 'SXRT' = 'SXRT'
  const normalizeTechnique = (techniqueValue: string): string => {
    const conformalTechniques = [
      '3D',
      '3D Conformal ARC',
      'Electrons',
      'Electron CT Plan',
      'Simple',
      'Clinical',
      'Conformal RT',
    ];
    const steroTechniques = ['SBRT VMAT', 'SBRT IMRT', 'SRS IMRT', 'SRS VMAT'];
    const imrtVmatTechniques = ['VMAT', 'IMRT', 'VMAT/IMRT', 'Tangential IMRT'];

    if (techniqueValue.includes('Brachy')) return 'Brachy';
    if (conformalTechniques.includes(techniqueValue)) return 'Conformal';
    if (steroTechniques.includes(techniqueValue)) return 'Stereotactic';
    if (imrtVmatTechniques.includes(techniqueValue)) return 'IMRT/VMAT';
    return techniqueValue;
  };

  return (careplanData as any)['careplans']
    .filter(
      (record: any) =>
        !tenantConfig.insightsExcludePhysicians.some(
          (excluded) => excluded.toLowerCase() === record.createdBy.toLowerCase(),
        ),
    )
    .map((record: any) => {
      const siteValues = record.sitegroupSet?.[0]?.siteSet?.[0]?.sitevaluesSet || [];
      const techniqueValue = getSiteValue(siteValues, 'Technique', '');
      const fractionsValue = parseFloat(getSiteValue(siteValues, 'Fractions', 0));

      // if there are multiple dose values, take the first one for calculating fraction dose
      const doseValue = parseFloat(
        getSiteValue(siteValues, 'Dose', '0').replace('[', '').replace(']', '').split(',')[0],
      );

      const createdBy = record.createdBy
        ? record.createdBy.replace(/(MD|DR|DOCTOR|PROF|PROFESSOR)\s*/gi, '').trim()
        : 'Unspecified';

      return {
        ...record,
        createdBy: createdBy,
        treatingDepartment: record.treatingDepartment || tenantConfig.insightsDefaultDepartment,
        diagnosisId: record.diagnosis?.id,
        diagnosisCode: `${record.diagnosisCode} ${record.diagnosis?.diagnosisCode?.diagnosisDescription || ''}`,
        tumourStream: normalizeTumourStream(record.diagnosis?.diagnosisCode?.tumourStream?.name || ''),
        technique: normalizeTechnique(techniqueValue),
        fractions: fractionsValue,
        fractionDose: doseValue / fractionsValue,
        adherence: true,
        linac: '',
        sim_to_treat: 0,
        referrer: '',
      };
    });
};

const processAppointments = (
  appointmentData: any[],
  processedCareplanData: any[],
  tenantConfig: TenantConfigType,
  user: any,
  isSuperuser: boolean,
) => {
  if (!appointmentData) {
    return [];
  }

  return (appointmentData as any)['doctorAppointmentsByDateWithPractitioners']
    .map((record: any) => {
      // assuming that we can match appointments to careplans by joining on patient id
      // this may work for simple sites, but likely need a more robust way to match these moving forward
      const careplan = processedCareplanData
        ? processedCareplanData.find((careplan) => careplan.patient?.id === record.appointment.patient?.id)
        : null;

      let physicianFullName = '';

      if (isSuperuser) {
        // assume first related practioner with relationshipType = 'oncologist' is the one assigned to the appointment
        const relatedOncologist = record.relatedPractitioners.find(
          (practitioner: any) => practitioner.relationshipType === 'oncologist',
        );

        const practitioner = record.appointment.practitioner
          ? record.appointment.practitioner
          : relatedOncologist?.practitioner;

        physicianFullName = practitioner ? `${practitioner.firstName} ${practitioner.lastName}` : 'Unspecified';
      } else {
        physicianFullName = user.name;
      }

      return {
        ...record,
        submittedAt: record.appointment.startTime,
        createdBy: physicianFullName,

        // if a matching careplan is not found, return the appointment with default values
        // TODO: needs to be dynamic once there is more than 1 site in a tenant
        treatingDepartment: tenantConfig?.insightsDefaultDepartment,
        tumourStream: careplan?.tumourStream || 'Unspecified',
      };
    })
    .filter(
      (record: any) =>
        !tenantConfig.insightsExcludePhysicians.some(
          (excluded) => excluded.toLowerCase() === record.createdBy.toLowerCase(),
        ),
    );
};

const appointmentsEndDate = new Date();

const InsightsDashboard = (): JSX.Element => {
  const history = useHistory();
  const theme = useTheme();
  const { state: userState } = useContext(UserContext);
  const permissions = userState.permissions;
  const { data: userData } = useQuery(INSIGHTS_USER_QUERY);

  const [dashboardData, setDashboardData] = useState<any>([]);
  const [rawCareplanData, setRawCareplanData] = useState<any>([]);
  const [dataRefreshed, setDataRefreshed] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState('index');

  const [openSections, setOpenSections] = useState<Record<SectionNames, boolean>>({
    Quality: true,
    Access: true,
    Efficiency: true,
    Activity: true,
  });

  const navToPage = (page: string) => {
    if (currentPage === page) {
      return;
    }

    if (page === 'index') {
      history.push('/radiation/insights');
    } else {
      history.push(`/radiation/insights/${page}`);
    }
    setCurrentPage(page);
  };

  const toggleSection = (section: SectionNames) => {
    setOpenSections((prevOpenSections) => ({
      ...prevOpenSections,
      [section]: !prevOpenSections[section],
    }));
  };

  // set current page
  useEffect(() => {
    const getPageName = (pathname: string) => {
      const parts = pathname.replace('/radiation/insights', '').split('/').filter(Boolean);
      return parts[0] || 'index';
    };

    setCurrentPage(getPageName(location.pathname));
    restoreScrollPosition(0);
    scrollPositionRef.current = 0;

    const unlisten = history.listen((location) => {
      setCurrentPage(getPageName(location.pathname));
    });

    return () => {
      unlisten();
    };
  }, [history, location.pathname]);

  // bunch of workarounds to save and restore scroll position when switching between pages
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  const saveScrollPosition = useCallback(() => {
    if (tableWrapperRef.current && tableWrapperRef.current.scrollTop !== 0) {
      scrollPositionRef.current = tableWrapperRef.current.scrollTop;
    }
  }, []);

  const restoreScrollPosition = (override?: number) => {
    if (tableWrapperRef.current) {
      requestAnimationFrame(() => {
        if (override || override === 0) {
          tableWrapperRef.current!.scrollTop = override;
        } else {
          tableWrapperRef.current!.scrollTop = scrollPositionRef.current;
        }
      });
    }
  };

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    if (tableWrapper) {
      tableWrapper.addEventListener('scroll', saveScrollPosition);
    }

    return () => {
      if (tableWrapper) {
        tableWrapper.removeEventListener('scroll', saveScrollPosition);
      }
    };
  }, [saveScrollPosition]);

  const { data: tenantConfigData, loading: tenantConfigLoading } = useQuery(TENANT_CONFIG_QUERY);

  // fetch careplan and appointment data
  const { data: careplanData, loading: careplansLoading } = useQuery(GET_CAREPLANS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      rowsPerPage: 9999,
      status: ['Plan Aim', 'Prescription', 'Prescription Directive'],
      onlyForRequestingPractitioner: false,
      insightsRequest: true,
    },
    skip: tenantConfigLoading || !tenantConfigData,
  });

  const { data: appointmentData, loading: appointmentsLoading } = useQuery(DOCTOR_APPOINTMENT_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      startDate: new Date(tenantConfigData?.tenantConfig.insightsDateRangeStart || DEFAULT_INSIGHTS_START_DATE),
      endDate: appointmentsEndDate,
      appointmentActivities: tenantConfigData?.tenantConfig.insightsSimAppointmentActivities.concat(
        tenantConfigData?.tenantConfig.insightsTreatmentAppointmentActivities,
      ),
      excludeCancelled: true,
      allPractitioners: true,
    },
    skip: careplansLoading || !careplanData,
  });

  // process data when careplan and appointment data is available
  useEffect(() => {
    if (CurrentAppConfig.Insights?.mockData) {
      const updatedData = processMockData(MockData, MockAppointmentData);
      setDashboardData(updatedData);
      setRawCareplanData(MockData); // Set raw mock careplan data
      return;
    }

    if (tenantConfigData && userData) {
      const processedCareplansData = processCareplans(careplanData, tenantConfigData?.tenantConfig);
      const processedAppointmentsData = processAppointments(
        appointmentData,
        processedCareplansData,
        tenantConfigData?.tenantConfig,
        userData?.user,
        permissions.includes(PermissionType.INSIGHTS_SUPERUSER_ACCESS),
      );

      setDashboardData([...processedCareplansData, ...processedAppointmentsData]);
      setRawCareplanData(processedCareplansData); // Set raw processed careplan data
      setDataRefreshed(true);
    }
  }, [tenantConfigData, userData, careplanData, appointmentData, permissions]);

  useEffect(() => {
    if (!userData || isDemo || LOCAL_ENV) {
      return;
    }
    const canAccessInsights =
      permissions.includes(PermissionType.INSIGHTS_ACCESS) ||
      permissions.includes(PermissionType.INSIGHTS_SUPERUSER_ACCESS);
    if (!canAccessInsights) {
      history.replace('/error');
    }
  }, [userData, history, permissions]);

  return (
    <DashboardContextProvider
      data={dashboardData}
      rawCareplanData={rawCareplanData}
      dataRefreshed={dataRefreshed}
      setDataRefreshed={setDataRefreshed}
      navToPage={navToPage}
      onChartClick={restoreScrollPosition}
      tenantConfig={tenantConfigData ? tenantConfigData.tenantConfig : {}}
      prescriptionsLoading={careplansLoading}
      userData={userData?.user}>
      <Stack direction="row" width="100%" height="100%">
        {currentPage !== 'index' && currentPage !== 'reports' && (
          <NavBar
            openSections={openSections}
            toggleSection={toggleSection}
            navToPage={navToPage}
            currentPage={currentPage}
            theme={theme}
          />
        )}
        <StyledTile sx={{ paddingTop: '20px', paddingBottom: '10px', margin: 0, boxShadow: 'none' }}>
          <Stack
            justifyContent="space-between"
            width="100%"
            height="80%"
            flexGrow={1}
            ref={tableWrapperRef}
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              marginRight: '-15px',
              paddingRight: '15px',
              '&::-webkit-scrollbar': {
                width: '8px',
                WebkitAppearance: 'none',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#cfcfcf',
                borderRadius: '8px',
                boxShadow: '0 0 1px rgba(255, 255, 255, 0.5)',
              },
            }}>
            {currentPage === 'index' && (
              <DashboardOverview
                careplansLoading={careplansLoading || !careplanData}
                appointmentsLoading={appointmentsLoading || !appointmentData}
                showReportsButton={permissions.includes(PermissionType.INSIGHTS_SUPERUSER_ACCESS)}
              />
            )}
            {currentPage === 'reports' && <Reports />}
            {currentPage === 'carepathway' && (
              <Dashboard
                title="Prescription Utilization"
                metric="Prescription Template Adherence"
                metricFunction={DataFunctions.calcAdherencePercentage}
                config={Config.CarepathUtilisation}
                metricSuffix="%"
              />
            )}
            {currentPage === 'techniques' && (
              <Dashboard
                title="Technique Utilization"
                metric="IMRT/VMAT/Stereotactic plans"
                metricFunction={DataFunctions.calcComplexTechnique}
                config={Config.TechniqueUtilisation}
                metricSuffix="%"
              />
            )}
            {currentPage === 'fractions' && (
              <Dashboard
                title="Fractionation Schedule"
                metric="Average Fractions"
                metricFunction={DataFunctions.calcAverageFractions}
                metricRound={1}
                config={Config.FractionationSchedule}
              />
            )}
            {currentPage === 'referrers' && (
              <Dashboard
                title="Top 10 Referrers"
                metric="Total Referrals"
                metricFunction={DataFunctions.calcTotalGeneric}
                config={Config.Top10Referrers}
              />
            )}
            {currentPage === 'diagnosis' && (
              <Dashboard
                title="Diagnosis"
                metric="Total Diagnoses"
                metricFunction={DataFunctions.calcTotalDiagnosis}
                config={Config.Diagnosis}
              />
            )}
            {currentPage === 'activity' && (
              <Dashboard
                title="Clinical Activity"
                metric="Total Plans"
                metricFunction={DataFunctions.calcTotalPlans}
                config={Config.ClinicalActivity}
              />
            )}
            {currentPage === 'consultations' && (
              <Dashboard
                title="Total Consultations"
                metric="Total Consultations"
                metricFunction={DataFunctions.calcTotalGeneric}
                config={Config.Consultations}
                dataType="AppointmentWithPractitionersType"
                subChartDataType="CarepathCareplanType"
                dataSource={tenantConfigData?.tenantConfig.insightsEmrDataSource || ''}
              />
            )}
            {currentPage === 'machines' && (
              <Dashboard
                title="Machine Utilization"
                metric="Average Daily Treatments Per Linac"
                metricFunction={DataFunctions.calcAverageDailyTreatmentsPerLinac}
                config={Config.MachineUtilisation}
                metricRound={1}
                dataType="AppointmentWithPractitionersType"
                subChartDataType="AppointmentWithPractitionersType"
                appointmentFilterFunction={isTreatmentAppointment}
                dataSource={tenantConfigData?.tenantConfig.insightsEmrDataSource || ''}
              />
            )}
            {currentPage === 'wait-times' && (
              <Dashboard
                title="Patient Wait Times"
                metric="Average Sim to Treatment Time (Days)"
                metricFunction={DataFunctions.calcAverageSimToTreat}
                config={Config.PatientWaitTimes}
                metricRound={1}
                dataType="AppointmentWithPractitionersType"
                dataSource={tenantConfigData?.tenantConfig.insightsEmrDataSource || ''}
                subChartDataType="AppointmentWithPractitionersType"
              />
            )}
            {currentPage === 'productivity' && (
              <Dashboard
                title="Planning Productivity"
                metric="Average Daily Plans"
                metricFunction={DataFunctions.calcAverageDailyPlans}
                config={Config.PlanningProductivity}
                metricRound={1}
              />
            )}
            {currentPage === 'simulations' && (
              <Dashboard
                title="Total Simulations"
                metric="Total Simulations"
                metricFunction={DataFunctions.calcTotalSimAppointments}
                config={Config.TotalSimulations}
                dataType="AppointmentWithPractitionersType"
                subChartDataType="AppointmentWithPractitionersType"
                appointmentFilterFunction={isSimAppointment}
                dataSource={tenantConfigData?.tenantConfig.insightsEmrDataSource || ''}
              />
            )}
            {currentPage === 'treatments' && (
              <Dashboard
                title="Total Treatments (Fractions)"
                metric="Total Treatments (Fractions)"
                metricFunction={DataFunctions.calcTotalTreatmentAppointments}
                config={Config.TotalTreatments}
                dataType="AppointmentWithPractitionersType"
                subChartDataType="AppointmentWithPractitionersType"
                appointmentFilterFunction={isTreatmentAppointment}
                dataSource={tenantConfigData?.tenantConfig.insightsEmrDataSource || ''}
              />
            )}
          </Stack>
        </StyledTile>
      </Stack>
    </DashboardContextProvider>
  );
};

export default InsightsDashboard;
